<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  field: any;
  rowData: any;
  settings: any;
}>();

// NOTE: We remove the link from AF Staff values as they are internal users and should not be linked to a profile.
const isAfStaff = computed(() => {
  const userStaffName = props.field;
  return userStaffName === 'AF Staff';
});

const facebookLink = computed(() => props.settings?.isFacebookLink && props.rowData.facebook_url);

const hasTargetKey = computed(() => {
  if (!props.settings?.render) return false;

  const { targetKey } = props.settings.render;

  if (targetKey.length > 1) {
    const primary = props.rowData[targetKey[0]];
    return (
      !!(primary && typeof primary === 'object' && primary[targetKey[1]]) ||
      !!(primary != null && (typeof primary === 'string' || typeof primary === 'number'))
    );
  }

  return !!props.rowData[targetKey[0]];
});

const targetRef = computed(() => {
  if (!props.settings?.render) return null;

  const { href, targetKey, secondaryKey } = props.settings.render;

  if (secondaryKey) {
    const primary = props.rowData[targetKey[0]];
    const secondary = props.rowData[secondaryKey[0]];

    if (primary != null && secondary != null) return `${href}${primary}/${secondary}`;

    return null;
  }

  if (targetKey.length > 1) {
    const primary = props.rowData[targetKey[0]];
    if (primary && typeof primary === 'object' && primary[targetKey[1]] != null) {
      const value = primary[targetKey[1]];
      if (targetKey[0] === 'by_id' && value === 'other') return null;

      return `${href}${value}`;
    }

    if (primary != null && (typeof primary === 'string' || typeof primary === 'number')) {
      if (targetKey[0] === 'by_id' && primary === 'other') return null;
      return `${href}${primary}`;
    }

    return null;
  }

  if (props.rowData[targetKey[0]] != null) {
    const value = props.rowData[targetKey[0]];
    if (targetKey[0] === 'by_id' && value === 'other') return null;

    return `${href}${value}`;
  }

  return null;
});
</script>

<template>
  <RouterLink
    v-if="!field && settings?.canBeUnknown && targetRef"
    class="af-link overflow-hidden hover:underline"
    :to="targetRef"
  >
    {{ $t('common.unknown') }}
  </RouterLink>

  <span
    v-else-if="!field"
    class="flex flex-col items-start gap-0.5"
  >
    {{ settings?.composedEmptyState ? $t(settings.composedEmptyState) : '-' }}
  </span>

  <a
    v-else-if="settings?.render?.type === 'mail'"
    class="af-link overflow-hidden hover:underline"
    target="_blank"
    :href="'mailto:' + field"
  >
    {{ field }}
  </a>

  <RouterLink
    v-else-if="targetRef && !isAfStaff"
    class="af-link overflow-hidden text-ellipsis hover:underline"
    :to="targetRef"
  >
    {{ settings.hasName ? field.name : field }}
  </RouterLink>

  <a
    v-else-if="facebookLink"
    class="af-link overflow-hidden text-ellipsis hover:underline"
    target="_blank"
    :href="facebookLink"
  >
    {{ $t('components.dataTable.facebookPage') }}
  </a>

  <p
    v-else-if="!hasTargetKey"
    class="overflow-hidden text-ellipsis"
  >
    {{ field }}
  </p>

  <p
    v-else
    class="overflow-hidden text-ellipsis"
  >
    {{ field || '-' }}
  </p>
</template>
